import { HsaQuestionGroupStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import HelpCircleIcon from "@redotech/redo-icons/help-circle.svg";
import { HSA_MAX_LIST_ROWS } from "@redotech/redo-web-automations/automations/utils/layout-elements";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Handle, Position } from "@xyflow/react";
import { memo } from "react";
import { PartialWithRequiredType } from "../steps";
import { BaseNode } from "./base-node";
import * as NodeCSS from "./node.module.css";

export interface HsaQuestionGroupNodeData {
  step: PartialWithRequiredType<HsaQuestionGroupStep>;
  deleteElements: (id: string) => void;
  readonly: boolean;
}

export const HsaQuestionGroupNode = memo(function HsaQuestionGroupNode({
  id,
  data,
  selected,
}: {
  id: string;
  data: HsaQuestionGroupNodeData;
  selected?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const questions = data.step.questions ?? [];
  const shown = questions.slice(0, HSA_MAX_LIST_ROWS);
  const overflow = questions.length - shown.length;

  return (
    <BaseNode
      containerClassName={NodeCSS.checkoutNode}
      icon={<RedoFeaturedIcon color="brand" Icon={HelpCircleIcon} size="sm" />}
      onDelete={data.readonly ? undefined : () => data.deleteElements(id)}
      selected={selected}
      step={data.step}
      title={data.step.title || t("Question group")}
    >
      <Handle id="top" position={Position.Top} type="target" />
      <Flex className={NodeCSS.fullWidth} dir="column" gap="xs">
        <Text fontSize="xxs" textColor="secondary">
          {t("{{count}} questions", { count: questions.length })}
        </Text>
        {shown.length ? (
          shown.map((entry, index) => (
            <Text
              className={NodeCSS.clampedValueText}
              key={entry?.handle ?? index}
            >
              {entry?.question?.label ?? entry?.handle}
            </Text>
          ))
        ) : (
          <Text className={NodeCSS.placeholderText}>{t("No questions")}</Text>
        )}
        {overflow > 0 && (
          <Flex w="full">
            <RedoBadge
              color="gray"
              size="xs"
              text={t("+{{count}} more questions", { count: overflow })}
            />
          </Flex>
        )}
      </Flex>
      <Handle id="bottom" position={Position.Bottom} type="source" />
    </BaseNode>
  );
});
